import { MutationFunctionOptions } from '@apollo/client';
import {
  ArticleEditorDocument,
  ArticleEditorQuery,
  ArticleEditorQueryVariables,
  ArticleTranslationDeleteMutation,
  LanguageId,
  useArticleTranslationDeleteMutation,
} from 'lib-knowledge-management-common/graphql';

type MutationFunction = ReturnType<
  typeof useArticleTranslationDeleteMutation
>[0];
type MutationReturnValue = ReturnType<MutationFunction>;

export type DeleteTranslationFn = () => MutationReturnValue;

type UseArticleUnpublishReturnValue = [
  DeleteTranslationFn,
  ReturnType<typeof useArticleTranslationDeleteMutation>[1],
];

type UseArticleUnpublishParams = {
  articleId: string;
  languageId: LanguageId;
  onCompleted?: MutationFunctionOptions<ArticleTranslationDeleteMutation>['onCompleted'];
};

export function useTranslationDelete({
  onCompleted,
  articleId,
  languageId,
}: UseArticleUnpublishParams): UseArticleUnpublishReturnValue {
  const [deleteTranslation, mutationResult] =
    useArticleTranslationDeleteMutation();

  const updateFn: DeleteTranslationFn = () => {
    return deleteTranslation({
      variables: {
        articleId,
        languageId,
      },
      onCompleted,
      optimisticResponse: {
        __typename: 'Mutations',
        articleTranslationDelete: null,
      },
      update: (cache) => {
        const cachedArticleEditorQuery = cache.readQuery<
          ArticleEditorQuery,
          ArticleEditorQueryVariables
        >({
          query: ArticleEditorDocument,
          variables: { id: articleId },
        });
        if (!cachedArticleEditorQuery?.article) return;

        const updatedArticleQuery: ArticleEditorQuery = {
          ...cachedArticleEditorQuery,
          article: {
            ...cachedArticleEditorQuery.article,
            translations: {
              ...cachedArticleEditorQuery.article.translations,
              draft: cachedArticleEditorQuery.article.translations.draft.filter(
                (t) => t.languageId !== languageId,
              ),
            },
          },
        };
        cache.writeQuery<ArticleEditorQuery, ArticleEditorQueryVariables>({
          query: ArticleEditorDocument,
          variables: { id: articleId },
          data: updatedArticleQuery,
        });

        cache.evict({
          id: cache.identify({
            __typename: 'ArticleTranslation',
            articleId,
            languageId,
            version: { __typename: 'Draft' },
          }),
        });
      },
    });
  };

  return [updateFn, mutationResult];
}
